// Speech error matrix (SPEECH §3.6): a server status or code, a capture code
// or a client condition → a copy key in ui/i18n/ns/speech.ts, and whether the
// audio is kept on the device for Retry. The server only ever sends codes.
import type { SpeechCopyKey } from '@/i18n/ns/speech';
import { CaptureError, SpeechHttpError } from './contracts';

export type SpeechIssue = {
  /** The code the issue came from (server, capture or client). */
  code: string;
  key: SpeechCopyKey;
  /** The audio stays in the spool and the notice offers Retry and Discard. */
  kept: boolean;
  /** Placeholder values for the copy (`{seconds}`). */
  values?: Record<string, string | number>;
};

type Entry = { key: SpeechCopyKey; kept: boolean };

const kept = (key: SpeechCopyKey): Entry => ({ key, kept: true });
const lost = (key: SpeechCopyKey): Entry => ({ key, kept: false });

const MATRIX: Record<string, Entry> = {
  // Server and HTTP codes (C-15).
  not_authenticated: kept('signInAgain'),
  rate_limited: kept('rateLimited'),
  budget_exhausted: kept('budgetExhausted'),
  speech_unavailable: kept('unavailable'),
  // Keep server startup failures distinct in the notice so users know whether
  // to fix the page origin or wait for the transcription provider.
  provider_unavailable: kept('providerUnavailable'),
  not_configured: kept('notConfigured'),
  session_expired: kept('unavailable'),
  timeout: kept('timeout'),
  too_long: kept('tooLong'),
  no_audio: lost('noAudio'),
  protocol: kept('unavailable'),
  internal: kept('unavailable'),
  invalid_request: kept('unavailable'),
  json_required: kept('unavailable'),
  request_failed: kept('offline'),
  request_origin_not_allowed: kept('originNotAllowed'),
  company_assignment_required: kept('companyRequired'),
  // Kept: once a card is added (billing opens), Try again sends it.
  payment_required: kept('paymentRequired'),
  replaced: kept('replaced'),
  // Client conditions.
  offline: kept('offline'),
  device_lost: kept('deviceLost'),
  hidden: kept('pausedHidden'),
  interrupted: kept('keptForRetry'),
  mic_held_by_meeting: lost('micHeldByMeeting'),
  // Capture codes.
  permission_denied: lost('permissionDenied'),
  insecure_context: lost('insecureContext'),
  no_microphone: lost('noMicrophone'),
  microphone_busy: lost('microphoneBusy'),
  unsupported: lost('unsupported'),
};

const DEFAULT_RETRY_AFTER_SEC = 60;

/** The issue for a code; unknown codes keep the audio and read as unavailable. */
export function speechIssue(code: string, retryAfterSec?: number | null): SpeechIssue {
  const entry = MATRIX[code] ?? kept('unavailable');
  return entry.key === 'rateLimited'
    ? {
        code,
        ...entry,
        values: { seconds: Math.max(1, Math.ceil(retryAfterSec ?? DEFAULT_RETRY_AFTER_SEC)) },
      }
    : { code, ...entry };
}

/** The issue for anything a speech call can throw. */
export function speechIssueFor(error: unknown): SpeechIssue {
  if (error instanceof SpeechHttpError) {
    const code =
      error.code === 'speech_unavailable' && error.reason === 'not_configured'
        ? 'not_configured'
        : error.code === 'speech_unavailable' && error.reason === 'provider_unavailable'
          ? 'provider_unavailable'
          : error.code;
    return speechIssue(code, error.retryAfterSec);
  }
  if (error instanceof CaptureError) return speechIssue(error.code);
  const code = (error as { code?: unknown } | null)?.code;
  return speechIssue(typeof code === 'string' ? code : 'internal');
}

/** The close codes C-4 reserves for a server-side end of the session. */
const CLOSE_CODE_ISSUES: Record<number, string> = {
  4400: 'protocol',
  4408: 'timeout',
  4413: 'too_long',
  4429: 'budget_exhausted',
};

/** The code for a terminal close, or null when the close should reconnect. */
export const terminalCloseCode = (code: number): string | null => CLOSE_CODE_ISSUES[code] ?? null;
